/*
datafeed.js

Custom datafeed that pulls in symbol information, historical quotes, fundamentals, and
recommendations to support charts and indicators the TradingView widget.

Reference:
https://www.tradingview.com/charting-library-docs/latest/tutorials/implement_datafeed_tutorial/Datafeed-Implementation/
 */
import {
  makeApiRequest,
} from './helpers.js';

const lastBarsCache = new Map();
let allBars = [];

// DatafeedConfiguration implementation
const configurationData = {
  // Represents the resolutions for bars supported by your datafeed
  supported_resolutions: ['1D'],
  supports_timescale_marks: true,
  supports_marks: true,

  // The `exchanges` arguments are used for the `searchSymbols` method if a user selects the exchange
  exchanges: [{
    value: 'NASDAQ',
    name: 'NASDAQ',
    desc: 'NASDAQ',
  },
    {
    value: 'NYSE',
    name: 'NYSE',
    desc: 'NYSE',
  },
  ],
  // The `symbols_types` arguments are used for the `searchSymbols` method if a user selects this symbol type
  symbols_types: [{
    name: 'stock',
    value: 'stock',
  },
  ],
};

export default {
  getAllBars: () => {
    return allBars;
  },

  onReady: (callback) => {
    console.log('[onReady]: Method call');
    setTimeout(() => callback(configurationData));
  },

  searchSymbols: async (
    userInput,
    exchange,
    symbolType,
    onResultReadyCallback,
  ) => {
    console.log('[searchSymbols]: Method call');
    let newSymbols = [];
    onResultReadyCallback(newSymbols);
  },

  resolveSymbol: async (
    symbolName,
    onSymbolResolvedCallback,
    onResolveErrorCallback,
    extension,
  ) => {
    console.log('[resolveSymbol]: Method call', symbolName);
    const requestedSymbolInfo = symbolName.split(':');

    if (!requestedSymbolInfo) {
      console.log('[resolveSymbol]: Cannot resolve symbol', symbolName);
      onResolveErrorCallback('cannot resolve symbol');
      return;
    }

    const requestedSymbolData = {
      symbol: requestedSymbolInfo[1],
      exchange: requestedSymbolInfo[0],
      type: 'stock',
    }

    const symbolInfo = {
      id: symbolName,
      ticker: symbolName,
      name: requestedSymbolData.symbol,
      type: 'stock',
      session: '24x7',
      timezone: 'Etc/UTC',
      exchange: requestedSymbolData.exchange,
      minmov: 1,
      pricescale: 100,
      has_intraday: false,
      has_no_volume: true,
      has_weekly_and_monthly: false,
      supported_resolutions: configurationData.supported_resolutions,
      volume_precision: 2,
      data_status: 'streaming',
    };

    console.log('[resolveSymbol]: Symbol resolved', symbolName);
    onSymbolResolvedCallback(symbolInfo);
  },
  getBars: async (symbolInfo, resolution, periodParams, onHistoryCallback, onErrorCallback) => {
    const { from, to, firstDataRequest } = periodParams;
    console.log('[getBars]: Method call', symbolInfo, resolution, from, to);

    const urlParameters = {
      from_timestamp: periodParams.from,
      to_timestamp: periodParams.to,
    };

    const query = Object.keys(urlParameters)
      .map(name => `${name}=${encodeURIComponent(urlParameters[name])}`)
      .join('&');

    // Call Insights endpoint to retrieve chart bar and recs data
    // (e.g. /insights/quotes/tradingview/historical/204577/?from_timestamp=1662076800&to_timestamp=1688083200)
    try {
      const data = await makeApiRequest(`/quotes/tradingview/historical/${symbolInfo.id}/?${query}`);

      if (data === undefined) {
        // "noData" should be set if there is no data in the requested period
        onHistoryCallback([], {
          noData: true,
        });
        return;
      }

      let bars = [];

      data.bars.forEach(apiBar => {
        if (apiBar.time >= from && apiBar.time < to) {
          let tvBarTime = apiBar.time * 1000; // tradingview expects unix time is ms
          let recs = data.recs.filter(item => { return item.time === apiBar.time; });
          bars = [...bars, {
            time: tvBarTime,
            low: apiBar.low,
            high: apiBar.high,
            open: apiBar.open,
            close: apiBar.close,
            volume: apiBar.volume,
            peRatio: apiBar.peRatio,
            psRatio: apiBar.psRatio,
            totalEnterpriseValue: apiBar.totalEnterpriseValue,
            recs,
          }];
        }
      });

      if (firstDataRequest) {
        lastBarsCache.set(symbolInfo.full_name, {
          ...bars[bars.length - 1],
        });
      }

      allBars = [...bars, ...allBars];

      console.log(`[getBars]: returned ${bars.length} bar(s)`);
      onHistoryCallback(bars, {
        noData: false,
      });
    } catch (error) {
      console.log('[getBars]: Get error', error);
      onErrorCallback(error);
    }
  },
  subscribeBars: (symbolInfo, resolution, onRealtimeCallback, subscriberUID, onResetCacheNeededCallback) => {
    console.log('[subscribeBars]: Method call with subscriberUID:', subscriberUID);
  },
  unsubscribeBars: (subscriberUID) => {
    console.log('[unsubscribeBars]: Method call with subscriberUID:', subscriberUID);
  },
  getTimescaleMarks: (symbolInfo, startDate, endDate, onDataCallback, resolution) => {
    console.log('getTimescaleMarks');
  },
  getMarks: (symbolInfo, startDate, endDate, onDataCallback, resolution) => {
    console.log('getMarks');
    const barsWithRecs = allBars.filter(bar => bar.recs.length > 0);
    let marks = [];
    let i = 0;
    for (let barWithRec of barsWithRecs) {
      if (barWithRec.recs.length > 0) {
        for (let r of barWithRec.recs) {
          let mark = {
            id: i++,
            time: barWithRec.time / 1000,
            text: `${r.service} (${r.rec_type})`,
            label: 'R',
            labelFontColor: 'white',
            minSize: 25,
            color: r.rec_type === 'buy' ? 'green' : 'red',
          };
          marks = [...marks, mark];
        }
      }
    }
    onDataCallback(marks);
  },
};
